// base
import { createAsyncThunk } from '@reduxjs/toolkit';

// api
import { noticeApi } from '../apis/notice.api';

// action
import { actions } from '../actions/notice.action';

import {
  CreateNoticeReply,
  ReqCreateNotice,
  ReqUpdateNotice,
  ReqUpdateNoticeReply,
  ResNoticeDetail,
  ResNotices
} from '../models/notice.model';
import qs from 'qs';
import { ResultBaseProps } from 'modules/common';
import { RootState } from 'modules/rootReducer';

/**
 * 공지사항 검색 (공백 검색시 전체 리스트)
 * */
export const getNotices = createAsyncThunk<ResNotices, void, { state: RootState }>(
  actions.GET_NOTICES,
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const request = getState().notice.fetchNoticesRequest;
      const query = qs.stringify(request);
      const response = await noticeApi.getNotices(query);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 공지사항 상세 조회
 * */
export const getNoticeDetail = createAsyncThunk<ResNoticeDetail, string>(
  actions.GET_NOTICE_DETAIL,
  async (noticeId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await noticeApi.getNoticeDetail(noticeId);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 공지사항 등록하기
 **/
export const createNotice = createAsyncThunk<ResultBaseProps, void, { state: RootState }>(
  actions.CREATE_NOTICE,
  async (_, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const detail = getState().notice.noticeDetail;
      const files = detail.files;

      if (!detail.title || !detail.content) {
        return rejectWithValue({ message: 'Please enter the required values.' });
      }

      const request: ReqCreateNotice = {
        title: detail.title,
        content: detail.content,
        category: detail.category
      };

      const formData = new FormData();

      formData.append('notice', JSON.stringify(request));

      if (files) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      }

      const response = await noticeApi.createNotice(formData);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 공지사항 수정
 **/
export const updateNotice = createAsyncThunk<ResultBaseProps, string, { state: RootState }>(
  actions.UPDATE_NOTICE,
  async (noticeId, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const detail = getState().notice.noticeDetail;

      const request: ReqUpdateNotice = {
        title: detail.title,
        content: detail.content,
        category: detail.category
      };

      // const query = qs.stringify(request);

      const response = await noticeApi.updateNotice(noticeId, request);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * 공지사항 삭제
 * */
export const deleteNotice = createAsyncThunk<ResultBaseProps, string>(
  actions.DELETE_NOTICE,
  async (id, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await noticeApi.deleteNotice(id);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// /**
//  * 공지사항 사진 조회
//  * */
// export const getNoticePic = createAsyncThunk<>(
//   actions.GET_NOTICE_PIC_DOWNLOAD,
//   async (data, { fulfillWithValue, rejectWithValue }) => {
//     try {
//       const response = await noticeApi.getNoticePicAPI(data);

//       const { code, isSuccess, message, resultList, resultObject, totalMap } = response;

//       return fulfillWithValue({
//         code,
//         isSuccess,
//         message,
//         resultList,
//         resultObject,
//         totalMap
//       });
//     } catch (error) {
//       return rejectWithValue(error);
//     }
//   }
// );

export const createNoticeReply = createAsyncThunk<ResultBaseProps, string, { state: RootState }>(
  actions.CREATE_NOTICE_REPLY,
  async (noticeId, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const detail = getState().notice.replyDetail;
      const request: CreateNoticeReply = {
        content: detail.content
      };

      const response = await noticeApi.creatNoticeReply(noticeId, request);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateNoticeReply = createAsyncThunk<ResultBaseProps, ReqUpdateNoticeReply>(
  actions.UPDATE_NOTICE_REPLY,
  async (data, { fulfillWithValue, rejectWithValue }) => {
    try {
      const query = qs.stringify(data.reply);
      const response = await noticeApi.updateNoticeReply(data.noticeId, query);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteNoticeReply = createAsyncThunk<ResultBaseProps, string>(
  actions.DELETE_NOTICE_REPLY,
  async (replyId, { fulfillWithValue, rejectWithValue }) => {
    try {
      const response = await noticeApi.deleteNoticeReply(replyId);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteNoticeAttachment = createAsyncThunk<
  ResultBaseProps,
  string,
  { state: RootState }
>(
  actions.DELETE_NOTICE_ATTACHMENT,
  async (fileId, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const noticeId = getState().notice.noticeDetail.id;
      const response = await noticeApi.deleteNoticeAttachment(noticeId, fileId);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createNoticeAttachment = createAsyncThunk<
  ResultBaseProps,
  File[],
  { state: RootState }
>(
  actions.CREATE_NOTICE_ATTACHMENT,
  async (files, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const noticeId = getState().notice.noticeDetail.id;
      const formData = new FormData();

      if (files) {
        files.forEach((file) => {
          formData.append('files', file);
        });
      }

      const response = await noticeApi.createNoticeAttachment(noticeId, formData);

      return fulfillWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
